/**
 * 🪞 MindsetWeeklyRecap — surfaces the AI-generated weekly Mindset recap
 *
 * Yuri spec 2026-02: "Chaque dimanche soir, l'IA résume les 7 réponses de la
 * semaine en 2 phrases (centre de gravité + insight). Ferme la boucle
 * introspective et boost la rétention."
 *
 * Two display modes:
 *   • compact (Dashboard banner — only when is_fresh=true)
 *   • full    (Mindset page card with summary + themes + insight)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ChevronRight, Compass, X, TrendingUp, ArrowRight } from 'lucide-react';
import { api, useLanguage } from '../context/AppContext';

const ORANGE_GLOW = '0 0 14px rgba(251,146,60,0.55), 0 0 4px rgba(245,158,11,0.45)';

export function MindsetWeeklyRecapBanner() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/mindset/weekly-recap').then((r) => {
      if (!alive) return;
      setData(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data?.recap || !data.is_fresh) return null;

  const goRecap = () => {
    navigate('/mindset?tab=recap');
  };

  return (
    <motion.button
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={goRecap}
      className="group w-full mb-4 flex items-stretch gap-3 rounded-xl border border-orange-400/50 bg-gradient-to-r from-orange-500/15 via-card to-amber-500/10 p-3 text-left transition-all hover:scale-[1.01]"
      style={{ boxShadow: ORANGE_GLOW }}
      data-testid="mindset-recap-banner"
    >
      <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 ring-1 ring-orange-400/60 flex items-center justify-center"
           style={{ boxShadow: '0 0 12px rgba(251,146,60,0.55)' }}>
        <Compass className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-[9px] uppercase tracking-[0.25em] font-bold text-orange-300">
            {fr ? 'Bilan Mindset hebdo' : 'Weekly Mindset recap'}
          </p>
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-orange-500/20 ring-1 ring-orange-400/50">
            <Sparkles className="w-2.5 h-2.5 text-orange-300" />
            <span className="text-[9px] font-black uppercase tracking-widest text-orange-200 leading-none">
              {fr ? 'Nouveau' : 'New'}
            </span>
          </span>
        </div>
        <p className="text-sm font-heading text-foreground leading-snug line-clamp-1">
          {data.recap.gravity_center || (fr ? 'Découvre ton centre de gravité' : 'Discover your center of gravity')}
        </p>
        <p className="text-[10px] mt-0.5 uppercase tracking-widest text-orange-300 font-bold">
          → {fr ? 'Ouvrir le bilan' : 'Open the recap'}
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-orange-300/70 self-center shrink-0" />
    </motion.button>
  );
}

export default function MindsetWeeklyRecap() {
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  const fetchRecap = useCallback(async () => {
    try {
      const r = await api.get('/mindset/weekly-recap');
      setData(r.data);
      // Mark as seen if there's a recap to show
      if (r.data?.recap && !r.data.seen) {
        api.post('/mindset/weekly-recap/seen').catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecap(); }, [fetchRecap]);

  if (loading) return null;
  if (!data?.recap) return null;
  if (hidden) return null;

  const r = data.recap;
  const periodLabel = fr
    ? `${r.monday?.slice(5)} → ${r.sunday?.slice(5)}`
    : `${r.monday?.slice(5)} → ${r.sunday?.slice(5)}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-5"
      data-testid="mindset-weekly-recap-card"
    >
      <div
        className="relative rounded-2xl border border-orange-400/40 bg-gradient-to-br from-zinc-950 via-zinc-900/95 to-orange-950/20 p-5"
        style={{ boxShadow: '0 0 18px rgba(251,146,60,0.35), inset 0 0 0 1px rgba(251,146,60,0.10)' }}
      >
        {/* Dismiss */}
        <button
          onClick={() => setHidden(true)}
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center transition-colors"
          aria-label="Dismiss"
          data-testid="mindset-recap-dismiss"
        >
          <X className="w-3.5 h-3.5 text-zinc-400" />
        </button>

        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div
            className="shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 ring-1 ring-orange-400/60 flex items-center justify-center"
            style={{ boxShadow: '0 0 14px rgba(251,146,60,0.6)' }}
          >
            <Compass className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0 pr-8">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-orange-300">
                {fr ? 'Bilan Mindset · semaine' : 'Mindset recap · week'}
              </p>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                {periodLabel}
              </span>
            </div>
            <h3 className="font-heading text-xl uppercase tracking-tight text-foreground leading-tight">
              {r.gravity_center || (fr ? 'Ton centre de gravité' : 'Your center of gravity')}
            </h3>
          </div>
        </div>

        {/* Summary — the 2 sentences */}
        {r.summary && (
          <p className="text-base text-foreground/95 leading-relaxed mb-4" data-testid="mindset-recap-summary">
            {r.summary}
          </p>
        )}

        {/* Dominant themes chips */}
        {Array.isArray(r.dominant_themes) && r.dominant_themes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {r.dominant_themes.slice(0, 4).map((t, i) => (
              <span
                key={i}
                className="px-2.5 py-1 rounded-full bg-orange-500/12 ring-1 ring-orange-400/30 text-[11px] font-bold text-orange-200"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Insight — 1 sentence forward-looking */}
        {r.insight && (
          <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-3 flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-100/90 leading-relaxed italic">
              {r.insight}
            </p>
          </div>
        )}

        {/* 🌸 Hormonal cycle context — only renders when the recap was
            generated for a woman on an active cycle. (Yuri Iter. 313/314) */}
        {r.cycle_context?.dominant_phase_label && (
          <div
            data-testid="mindset-recap-cycle-context"
            className="mt-3 rounded-xl p-3 flex items-start gap-2.5"
            style={{
              border: '1px solid rgba(192,132,252,0.40)',
              background: 'linear-gradient(90deg, rgba(192,132,252,0.10), rgba(232,121,249,0.06))',
              boxShadow: '0 0 14px rgba(192,132,252,0.28), inset 0 0 0 1px rgba(232,121,249,0.10)',
            }}
          >
            <div
              className="w-7 h-7 rounded-lg bg-fuchsia-500/15 flex items-center justify-center flex-shrink-0"
              style={{ boxShadow: '0 0 8px rgba(192,132,252,0.5)' }}
              aria-hidden="true"
            >
              <Sparkles className="w-3.5 h-3.5 text-fuchsia-300" />
            </div>
            <div className="min-w-0">
              <p
                className="text-[10px] uppercase tracking-[0.25em] font-bold text-fuchsia-300 mb-0.5"
                style={{ textShadow: '0 0 6px rgba(232,121,249,0.6)' }}
              >
                {fr ? 'Contexte hormonal' : 'Hormonal context'}
              </p>
              <p className="text-xs text-foreground/85 leading-relaxed">
                {fr
                  ? `Semaine majoritairement en phase ${r.cycle_context.dominant_phase_label.toLowerCase()} (${r.cycle_context.dominant_phase_share}%). C'est physiologique, pas un échec.`
                  : `Week mostly in ${r.cycle_context.dominant_phase_label.toLowerCase()} phase (${r.cycle_context.dominant_phase_share}%). It's physiological, not a failure.`}
              </p>
            </div>
          </div>
        )}

        {/* 🚨 Physiology compassion message — shown only when the AI detected
            a stress/discipline-dip pattern coinciding with the luteal/pre-menstrual
            phase. The most concrete output of the Hub × Mindset coupling. */}
        {r.physiology_message && (
          <div
            data-testid="mindset-recap-physiology-message"
            className="mt-3 rounded-xl p-4 flex items-start gap-3"
            style={{
              border: '1px solid rgba(244,114,182,0.55)',
              background: 'linear-gradient(135deg, rgba(244,114,182,0.18) 0%, rgba(192,132,252,0.12) 60%, rgba(232,121,249,0.10) 100%)',
              boxShadow: '0 0 22px rgba(244,114,182,0.40), inset 0 0 0 1px rgba(244,114,182,0.18)',
            }}
          >
            <div
              className="w-9 h-9 rounded-xl bg-rose-500/20 flex items-center justify-center flex-shrink-0"
              style={{ boxShadow: '0 0 12px rgba(244,63,94,0.55)' }}
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-rose-300" fill="currentColor"
                style={{ filter: 'drop-shadow(0 0 6px rgba(244,63,94,0.85))' }}>
                <path d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z" />
              </svg>
            </div>
            <div className="min-w-0">
              <p
                className="text-[10px] uppercase tracking-[0.28em] font-bold text-rose-200 mb-1"
                style={{ textShadow: '0 0 6px rgba(244,114,182,0.7)' }}
              >
                {fr ? 'Lecture physiologique' : 'Physiological reading'}
              </p>
              <p className="text-sm text-foreground/95 leading-relaxed font-medium">
                {r.physiology_message}
              </p>
            </div>
          </div>
        )}

        {/* Footer meta */}
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3 text-right">
          {r.entries_analyzed}{' '}
          {fr
            ? (r.entries_analyzed > 1 ? 'réflexions analysées' : 'réflexion analysée')
            : (r.entries_analyzed > 1 ? 'reflections analyzed' : 'reflection analyzed')}
        </p>
      </div>
    </motion.div>
  );
}


// ============================================================================
// MONTHLY RECAP — évolution de l'OS mental sur 4 semaines
// ============================================================================

export function MindsetMonthlyRecapBanner() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/mindset/monthly-recap').then((r) => {
      if (!alive) return;
      setData(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data?.recap || !data.is_fresh) return null;

  return (
    <motion.button
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => navigate('/mindset')}
      className="group w-full mb-4 flex items-stretch gap-3 rounded-xl border border-amber-400/60 bg-gradient-to-r from-amber-500/15 via-card to-orange-500/12 p-3 text-left transition-all hover:scale-[1.01]"
      style={{ boxShadow: '0 0 16px rgba(245,158,11,0.55), 0 0 4px rgba(251,146,60,0.45)' }}
      data-testid="mindset-monthly-banner"
    >
      <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 ring-1 ring-amber-400/60 flex items-center justify-center"
           style={{ boxShadow: '0 0 14px rgba(245,158,11,0.6)' }}>
        <TrendingUp className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-[9px] uppercase tracking-[0.25em] font-bold text-amber-300">
            {fr ? 'Bilan Mindset · mois' : 'Monthly Mindset recap'}
          </p>
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/20 ring-1 ring-amber-400/50">
            <Sparkles className="w-2.5 h-2.5 text-amber-300" />
            <span className="text-[9px] font-black uppercase tracking-widest text-amber-200 leading-none">
              {fr ? 'Nouveau' : 'New'}
            </span>
          </span>
        </div>
        <p className="text-sm font-heading text-foreground leading-snug line-clamp-1">
          {data.recap.starting_state}
          {' '}→{' '}
          {data.recap.ending_state}
        </p>
        <p className="text-[10px] mt-0.5 uppercase tracking-widest text-amber-300 font-bold">
          → {fr ? "Découvre l'évolution de ton OS mental" : 'See your mental OS evolution'}
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-amber-300/70 self-center shrink-0" />
    </motion.button>
  );
}

export function MindsetMonthlyRecap() {
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  const fetchRecap = useCallback(async () => {
    try {
      const r = await api.get('/mindset/monthly-recap');
      setData(r.data);
      if (r.data?.recap && !r.data.seen) {
        api.post('/mindset/monthly-recap/seen').catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecap(); }, [fetchRecap]);

  if (loading) return null;
  if (!data?.recap) return null;
  if (hidden) return null;

  const r = data.recap;
  const FR_MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthName = (() => {
    try {
      const [y, m] = r.month_id.split('-');
      const idx = parseInt(m, 10) - 1;
      return `${(fr ? FR_MONTHS : EN_MONTHS)[idx]} ${y}`;
    } catch { return r.month_id; }
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-5"
      data-testid="mindset-monthly-recap-card"
    >
      <div
        className="relative rounded-2xl border border-amber-400/40 bg-gradient-to-br from-zinc-950 via-zinc-900/95 to-amber-950/25 p-5"
        style={{ boxShadow: '0 0 22px rgba(245,158,11,0.42), inset 0 0 0 1px rgba(245,158,11,0.12)' }}
      >
        <button
          onClick={() => setHidden(true)}
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center transition-colors"
          aria-label="Dismiss"
          data-testid="mindset-monthly-dismiss"
        >
          <X className="w-3.5 h-3.5 text-zinc-400" />
        </button>

        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div
            className="shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 ring-1 ring-amber-400/60 flex items-center justify-center"
            style={{ boxShadow: '0 0 16px rgba(245,158,11,0.7)' }}
          >
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0 pr-8">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-300 mb-0.5">
              {fr ? 'Bilan Mindset · mois' : 'Monthly Mindset recap'}
            </p>
            <h3 className="font-heading text-xl uppercase tracking-tight text-foreground leading-tight">
              {monthName}
            </h3>
          </div>
        </div>

        {/* Evolution arc — start → end */}
        <div className="rounded-xl bg-zinc-900/70 ring-1 ring-amber-400/20 p-3 mb-4">
          <p className="text-[9px] uppercase tracking-[0.25em] font-bold text-amber-400 mb-2">
            {fr ? 'Évolution de ton OS mental' : 'Your mental OS evolution'}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1.5 rounded-lg bg-zinc-800 ring-1 ring-zinc-700 text-sm font-bold text-zinc-300">
              {r.starting_state || '—'}
            </span>
            <ArrowRight className="w-4 h-4 text-amber-400 shrink-0" />
            <span
              className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500/30 to-orange-500/25 ring-1 ring-amber-400/60 text-sm font-bold text-amber-100"
              style={{ boxShadow: '0 0 10px rgba(245,158,11,0.45)' }}
            >
              {r.ending_state || '—'}
            </span>
          </div>
          {r.shift_summary && (
            <p className="text-sm text-amber-100/95 leading-relaxed mt-3 italic">
              "{r.shift_summary}"
            </p>
          )}
        </div>

        {/* Summary 2 phrases */}
        {r.summary && (
          <p className="text-base text-foreground/95 leading-relaxed mb-4" data-testid="mindset-monthly-summary">
            {r.summary}
          </p>
        )}

        {Array.isArray(r.dominant_themes) && r.dominant_themes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {r.dominant_themes.slice(0, 4).map((t, i) => (
              <span
                key={i}
                className="px-2.5 py-1 rounded-full bg-amber-500/12 ring-1 ring-amber-400/30 text-[11px] font-bold text-amber-200"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {r.insight && (
          <div className="rounded-xl border border-orange-400/30 bg-orange-500/5 p-3 flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
            <p className="text-sm text-orange-100/90 leading-relaxed italic">
              {r.insight}
            </p>
          </div>
        )}

        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3 text-right">
          {r.entries_analyzed}{' '}
          {fr
            ? (r.entries_analyzed > 1 ? 'réflexions analysées' : 'réflexion analysée')
            : (r.entries_analyzed > 1 ? 'reflections analyzed' : 'reflection analyzed')}
        </p>
      </div>
    </motion.div>
  );
}
